import classNames from 'classnames';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {showAuthDialog} from 'modules/auth/thunks';
import {IProductShort} from 'modules/products/types';
import {
    useAddToWishListMutation,
    useGetWishListArticlesQuery,
    useRemoveFromWishListMutation,
} from 'modules/wish-list/services';
import React, {FC, useCallback} from 'react';
import style from './style.module.scss';
import {IAnalyticsMeta, TPropsWithClassName} from 'core/types';
import Icon from 'components/Icon';
import {useProductWishlistGtag} from 'modules/products/hooks/gtag/useProductWishlistGtag';
import {EventAction} from 'modules/google-analytics/constants';
import {useIsLoggedIn} from 'modules/user/hooks/useIsLoggedIn';
import {ButtonNew} from 'components/ButtonNew';

interface IProps {
    isFavourite?: boolean;
    onClick?: () => void;
    id?: IProductShort['id'];
    product?: Partial<IProductShort>;
    analytics?: IAnalyticsMeta;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const AddToFavoritesButton: FC<TPropsWithClassName<IProps>> = ({onClick, id, className, product, analytics}) => {
    const isLoggedIn = useIsLoggedIn();
    const dispatch = useAppDispatch();

    const productWishlistGtag = useProductWishlistGtag();

    const {data} = useGetWishListArticlesQuery(undefined, {
        skip: !isLoggedIn,
    });
    const [addToWishList, {isLoading: isAddLoading}] = useAddToWishListMutation();
    const [removeFromWishList, {isLoading: isRemoveLoading}] = useRemoveFromWishListMutation();

    const products = data?.items;
    const isInWishList = products?.find((item) => item === id);

    const handleClick = useCallback(() => {
        const productWithId = {...product, id};
        if (!isLoggedIn) {
            dispatch(showAuthDialog);
        } else if (isInWishList) {
            removeFromWishList({id}).then(() => productWishlistGtag(productWithId, analytics, EventAction.Click));
        } else {
            if (product) {
                productWishlistGtag(product, analytics);
            }
            addToWishList({id});
        }
        onClick?.();
    }, [
        addToWishList,
        id,
        dispatch,
        productWishlistGtag,
        isInWishList,
        isLoggedIn,
        onClick,
        product,
        removeFromWishList,
        analytics,
    ]);

    return (
        <ButtonNew
            className={classNames(
                style.button,
                {[style.favouriteFilled]: isInWishList && isLoggedIn},
                className,
                'add-to-favorite-button',
            )}
            isLoading={isAddLoading || isRemoveLoading}
            onClick={handleClick}
            theme={'ghost'}
            data-index={analytics?.positionIndex}
            variants={{MB: 'small', TB: 'large', DT: 'large'}}
            isSquare={true}
            isIconOnly={true}
        >
            {isInWishList && isLoggedIn ? <Icon icon={'favourite-fill'} /> : <Icon icon={'favourite'} />}
        </ButtonNew>
    );
};
